import { getApiErrorMessage } from '@moko/utils';
import type {
  IPricingItem,
  IPricingRateBody,
} from '../../services/pricingService';
import { toDateOnly } from './pricingSettings';

export const ANY_CONDITION = 'none';

export const anyConditionOption = { key: ANY_CONDITION, value: 'Any' };

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const checked = (value: unknown) => value === true || value === 'true';

export const conditionId = (value: unknown): number | null => {
  if (value == null || value === '' || value === ANY_CONDITION) return null;
  const id = typeof value === 'number' ? value : Number(text(String(value)));
  return Number.isInteger(id) && id > 0 ? id : null;
};

export const conditionKey = (value: number | null | undefined) =>
  value == null ? ANY_CONDITION : String(value);

// An item tied to a time service cannot have a different turnaround condition.
// Keep an existing unrestricted rate unrestricted to preserve rate precedence.
export const rateTimeServiceId = (
  item: Pick<IPricingItem, 'timeServiceId'> | undefined,
  value: unknown
): number | null => {
  const condition = conditionId(value);
  return condition == null ? null : item?.timeServiceId ?? condition;
};

export const conditionOptions = (
  rows: { id?: number; name?: string; code?: string; sortOrder?: number }[]
) => [
  anyConditionOption,
  ...rows
    .flatMap((row) =>
      typeof row.id === 'number' ? [{ ...row, id: row.id }] : []
    )
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
    .map((row) => ({
      key: String(row.id),
      value: row.name || row.code || String(row.id),
    })),
];

export const conditionNames = (
  rows: { id?: number; name?: string; code?: string }[]
) => {
  const names: Record<number, string> = {};
  rows.forEach((row) => {
    if (typeof row.id === 'number') names[row.id] = row.name || row.code || '';
  });
  return names;
};

export const conditionLabel = (
  id: number | null | undefined,
  names: Record<number, string>
) => (id == null ? 'Any' : names[id] ?? String(id));

export const formatRateAmount = (
  amount: number,
  currencyCode: string,
  calculationMethod?: string | null
) => {
  if (calculationMethod === 'Percentage') return `${amount}%`;
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  } catch {
    return `${amount} ${currencyCode}`.trim();
  }
};

export type PricingRateBodyResult =
  | { ok: true; body: IPricingRateBody }
  | { ok: false; error: string };

export const pricingRateBody = (
  item: Record<string, unknown>
): PricingRateBodyResult => {
  const pricingItemId = Number(item.pricingItemId);
  const currencyCode = text(item.amount_Unit) || text(item.currencyCode);
  const amount = Number(item.amount);
  const effectiveFrom = text(item.effectiveFrom);
  const effectiveTo = text(item.effectiveTo);
  if (
    !Number.isInteger(pricingItemId) ||
    pricingItemId <= 0 ||
    !currencyCode ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !effectiveFrom
  ) {
    return {
      ok: false,
      error: 'Pricing item, currency, amount, and effective from are required.',
    };
  }
  return {
    ok: true,
    body: {
      pricingItemId,
      currencyCode,
      amount,
      effectiveFrom: toDateOnly(effectiveFrom),
      effectiveTo: effectiveTo ? toDateOnly(effectiveTo) : null,
      workTypeId: conditionId(item.workTypeId),
      billingClassId: conditionId(item.billingClassId),
      timeServiceId: conditionId(item.timeServiceId),
      isDiscountable: checked(item.isDiscountable),
    },
  };
};

const API_STATUS = /API Error: (\d{3})/;

const messagesFromErrors = (errors: unknown): string[] => {
  if (!errors || typeof errors !== 'object') return [];
  return Object.values(errors as Record<string, unknown>).flatMap((value) => {
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is string =>
        typeof item === 'string' && item.trim().length > 0
    );
  });
};

export const pricingRateErrorMessage = (error: unknown) => {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string'
      ? error.message
      : '';
  const status = Number(API_STATUS.exec(raw)?.[1]);
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(jsonStart)) as {
        detail?: unknown;
        errors?: unknown;
      };
      const fieldErrors = messagesFromErrors(parsed.errors);
      if (fieldErrors.length) return fieldErrors.join(' ');
      if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
        return parsed.detail.trim();
      }
    } catch {
      // Fall through to the shared reader.
    }
  }
  if (status === 409) {
    return 'This rate overlaps an existing rate for the same pricing item and conditions.';
  }
  return getApiErrorMessage(error, 'Could not save the pricing rate.');
};
